import React, { Component } from 'react';
import { Button } from 'react-bootstrap';
import { Step, StepLabel, Stepper, TextField, Autocomplete, Checkbox } from '@mui/material';
import {
    updateClientImportTemplate,
    createClientImportTemplate,
    getClientImportTemplateDataById,
    uploadTemplateFile
} from '../../../../services/clinicPortalServices/clientImportTemplateService.js';
import {
    getSystemFieldCategory,
    getSystemColumnField,
    getUserID,
    sanitizeColumnName
} from '../../../../services/common/util.js';
import toastr from 'toastr';
import Papa from 'papaparse';
import { AgGridReact } from 'ag-grid-react';
import { colorSchemeDark, colorSchemeLight, themeQuartz } from 'ag-grid-community';
import { ThemeContext } from '../../../../theme/ThemeProvider';
import { getAllTestsForManagementPage } from '../../../../services/clinicPortalServices/testService.js';
import { fetchInstruments } from '../../../../services/clinicPortalServices/instrumentService.js';
import Swal from 'sweetalert2';
import readXlsxFile from 'read-excel-file';

const themeLight = themeQuartz.withPart(colorSchemeLight);
const themeDark = themeQuartz.withPart(colorSchemeDark);

export default class ClientImportTemplateDetails extends Component {
    constructor(props) {
        super(props);
        this.state = {
            clientImportTemplateId:
                this.props && this.props.clientImportTemplateId ? this.props.clientImportTemplateId : '',
            errors: [],
            steps: ['Upload Template', 'Analyze Columns', 'Configure Tests', 'Map Fields', 'Review & Save'],
            activeStep: 0,
            templateName: '',
            sourceFileName: '',
            originalSize: 0,
            fileRows: [],
            rowCount: 0,
            columnCount: 0,
            parsedData: [],
            columnAnalysis: [],
            totalColumns: 0,
            autoDetected: 0,
            requiredFields: 0,
            allTests: [],
            selectedTests: [],
            sourceAnalytes: [],
            analyteMappings: [],
            columnNameMap: {}, // { sanitized: original }
            isFinished: false,
            file: null,
            headerRowIndex: 0, // New state for specifying which row contains headers
            showHeaderRowInput: false, // Toggle for showing the header row input
            allInstruments: [],
            selectedInstrument: null
        };
    }

    componentDidMount() {
        if (this.state.clientImportTemplateId) {
            this.loadDetails();
        }
        // Load tests on component mount
        this.loadTests();
        // Load instruments
        this.loadInstruments();
    }

    loadTests = () => {
        getAllTestsForManagementPage({ isQuantitativeTest: true, isPanel: 'ignore' }).then((response) => {
            if (response?.data?.length > 0) {
                const allTemplates = this.props.context.componentParent.state.rowData;
                let existingTestIds = [];
                if (allTemplates && allTemplates.length > 0) {
                    allTemplates.forEach((row) => {
                        if (row.associatedTests?.length > 0) {
                            const testIds =
                                this.state.clientImportTemplateId === row._id
                                    ? []
                                    : row.associatedTests.map((test) => test._id);
                            existingTestIds = [...existingTestIds, ...testIds];
                        }
                    });
                }
                this.setState({ allTests: response.data.filter((test) => !existingTestIds.includes(test._id)) });
            }
        });
    };

    loadInstruments = () => {
        fetchInstruments().then((response) => {
            if (response?.data?.length > 0) {
                this.setState({ allInstruments: response.data });
            }
        });
    };

    loadDetails = () => {
        getClientImportTemplateDataById(this.state.clientImportTemplateId).then((res) => {
            const clientImportTemplateInfo = res.data;
            // Defensive: handle missing/legacy fields
            const columnAnalysis = (clientImportTemplateInfo.columnMappings || []).map((col, index) => ({
                index: col.columnIndex ?? index,
                sourceColumnName: col.sourceColumnName,
                sourceColumnField: col.sourceColumnField,
                systemColumnName: col.systemColumnName,
                systemColumnField: col.systemColumnField,
                dataType: col.dataType,
                required: col.validation?.required || false,
                manualEntry: col.manualEntry?.enabled || false,
                exampleValues: col.exampleValues || [],
                values: (col.exampleValues || []).join(', ')
            }));
            this.setState({
                templateName: clientImportTemplateInfo.name || '',
                sourceFileName: clientImportTemplateInfo.sourceFileName || '',
                originalSize: clientImportTemplateInfo.fileInfo?.originalSize || 0,
                rowCount: clientImportTemplateInfo.fileInfo?.rowCount || 0,
                columnCount: clientImportTemplateInfo.fileInfo?.columnCount || 0,
                headerRowIndex: clientImportTemplateInfo.fileInfo?.headerRowIndex || 0,
                columnAnalysis,
                totalColumns: columnAnalysis.length,
                autoDetected: columnAnalysis.filter((col) => col.systemColumnName !== 'None').length,
                requiredFields: columnAnalysis.filter((col) => col.required).length,
                columnNameMap: columnAnalysis.reduce((map, col) => {
                    map[col.sourceColumnField] = col.sourceColumnName;
                    return map;
                }, {}),
                fileRows: clientImportTemplateInfo.fileInfo?.fileRows || [],
                selectedTests: clientImportTemplateInfo.associatedTests || [],
                analyteMappings: (clientImportTemplateInfo.analyteMappings || []).map((mapping) => ({
                    sourceAnalyte: mapping.sourceAnalyte,
                    systemAnalyteId: mapping.systemAnalyteId?._id,
                    systemAnalyte: mapping.systemAnalyteId?.name || ''
                })),
                isFinished: clientImportTemplateInfo.status === 'active',
                activeStep: clientImportTemplateInfo.activeStep || 0,
                selectedInstrument: clientImportTemplateInfo.instrumentId || null
            });
        });
    };

    handleChange = (e) => {
        this.setState({
            [e.target.name]: e.target.value
        });
    };

    hasError = (key) => {
        return this.state.errors.indexOf(key) !== -1;
    };

    getSystemField = (columnName) => {};

    saveChanges = (isFinished = false) => {
        let errors = [];
        if (!this.state.templateName) {
            Swal.fire({
                icon: 'warning',
                title: 'Name Required',
                text: 'Please enter a template name to proceed.',
                confirmButtonColor: '#007bff',
                customClass: {
                    popup: 'swal2-border-radius'
                }
            });
            errors.push('templateName');
        }
        if (errors.length > 0) {
            return false;
        }
        const analyteNameSourceColumn = this.state.columnAnalysis.find(
            (col) => col.systemColumnName === 'Analyte Name'
        )?.sourceColumnName;
        const analyteUnitsSourceColumn = this.state.columnAnalysis.find(
            (col) => col.systemColumnName === 'Analyte Units'
        )?.sourceColumnName;
        let clientImportTemplateInfo = {
            name: this.state.templateName,
            sourceFileName: this.state.sourceFileName,
            fileInfo: {
                originalSize: this.state.originalSize,
                rowCount: this.state.rowCount,
                columnCount: this.state.columnCount,
                headerRowIndex: this.state.headerRowIndex,
                fileRows: this.state.fileRows
            },
            columnMappings: this.state.columnAnalysis.map((col, index) => {
                return {
                    columnIndex: index,
                    sourceColumnName: col.sourceColumnName,
                    sourceColumnField: col.sourceColumnField,
                    systemColumnName: col.systemColumnName,
                    systemColumnField: getSystemColumnField(col),
                    systemFieldCategory: getSystemFieldCategory(col.systemColumnName),
                    // sourceColumnField: col.sourceColumnField,
                    dataType: col.dataType,
                    validation: {
                        required: col.required
                    },
                    manualEntry: {
                        enabled: col.manualEntry
                    },
                    exampleValues: col.exampleValues
                };
            }),
            specialColumns: {
                sample: this.state.columnAnalysis.find((col) => col.systemColumnName === 'Sample ID')?.index,
                sampleType: this.state.columnAnalysis.find((col) => col.systemColumnName === 'Sample Type')?.index,
                collected: this.state.columnAnalysis.find((col) => col.systemColumnName === 'Collection Date')?.index,
                analyteName: this.state.columnAnalysis.find((col) => col.systemColumnName === 'Analyte Name')?.index,
                analyteResult: this.state.columnAnalysis.find((col) => col.systemColumnName === 'Analyte Result')
                    ?.index,
                analyteUnits: this.state.columnAnalysis.find((col) => col.systemColumnName === 'Analyte Units')?.index
            },
            associatedTests: this.state.selectedTests.map((test) => test._id),
            instrumentId: this.state.selectedInstrument?._id || null,
            analyteMappings: this.state.analyteMappings.map((mapping) => {
                const fileRow = this.state.fileRows.find(
                    (row) => row[analyteNameSourceColumn] === mapping.sourceAnalyte
                );
                const analyteUnits = fileRow ? fileRow[analyteUnitsSourceColumn] : '';
                return {
                    sourceAnalyte: mapping.sourceAnalyte,
                    systemAnalyteId: mapping.systemAnalyteId,
                    analyteUnits
                };
            }),
            status: isFinished ? 'active' : 'draft',
            activeStep: isFinished ? 0 : this.state.activeStep,
            createdBy: getUserID(),
            modifiedBy: getUserID()
        };
        if (this.state.clientImportTemplateId) {
            clientImportTemplateInfo._id = this.state.clientImportTemplateId;
            updateClientImportTemplate(clientImportTemplateInfo).then((res) => {
                const tempmlateInfo = res.data;
                if (tempmlateInfo) {
                    if (tempmlateInfo === 'name_exists') {
                        Swal.fire({
                            icon: 'warning',
                            title: 'Template Name Exists',
                            text: 'Please enter a different template name to proceed.',
                            confirmButtonColor: '#007bff',
                            customClass: {
                                popup: 'swal2-border-radius'
                            }
                        });
                    } else {
                        this.props.handleClose();
                        this.props.context.componentParent.loadGridData();
                        toastr.success('ClientImportTemplate is updated successfully');
                    }
                }
            });
        } else {
            createClientImportTemplate(clientImportTemplateInfo).then((res) => {
                const tempmlateInfo = res.data;
                if (tempmlateInfo) {
                    if (tempmlateInfo === 'name_exists') {
                        Swal.fire({
                            icon: 'warning',
                            title: 'Template Name Exists',
                            text: 'Please enter a different template name to proceed.',
                            confirmButtonColor: '#007bff',
                            customClass: {
                                popup: 'swal2-border-radius'
                            }
                        });
                    } else {
                        this.props.handleClose();
                        this.props.context.componentParent.loadGridData();
                        toastr.success('ClientImportTemplate is created successfully');
                    }
                }
            });
        }
    };

    uploadFile = async () => {
        let formData = new FormData();
        formData.append('files', this.state.file);
        await uploadTemplateFile(formData);
    };

    uploadTemplate = async (e) => {
        const file = e.target.files[0];

        if (!file) {
            return;
        }

        // Store the file for potential reprocessing
        this.setState({ file: file });

        let formData = new FormData();
        formData.append('files', file);
        const fileName = await uploadTemplateFile(formData);
        const originalSize = file.size;
        const fileExtension = fileName.split('.').pop().toLowerCase();

        // Auto-generate template name from sourceFileName if not already set
        const templateName = this.state.templateName || fileName.replace(/\.[^/.]+$/, '');

        try {
            if (fileExtension === 'csv') {
                // First, parse without headers to detect if there are header rows
                Papa.parse(file, {
                    header: false,
                    complete: (results) => {
                        const allRows = results.data;

                        // Store all rows temporarily
                        this.setState({
                            sourceFileName: fileName,
                            originalSize: originalSize,
                            templateName: templateName,
                            showHeaderRowInput: true,
                            allRawRows: allRows // Store all rows for reprocessing
                        });

                        // Process with default header row (0)
                        this.processCSVWithHeaderRow(allRows, 0);
                    },
                    error: (error) => {
                        toastr.error('Error parsing CSV file: ' + error.message);
                    }
                });
            } else if (fileExtension === 'xlsx' || fileExtension === 'xls') {
                // Parse Excel file using read-excel-file
                try {
                    const excelRows = await readXlsxFile(file);

                    if (excelRows.length === 0) {
                        toastr.error('Excel file appears to be empty');
                        return;
                    }

                    // Store all rows
                    this.setState({
                        sourceFileName: fileName,
                        originalSize: originalSize,
                        templateName: templateName,
                        showHeaderRowInput: true,
                        allRawRows: excelRows // Store all rows for reprocessing
                    });

                    // Process with default header row (0)
                    this.processExcelWithHeaderRow(excelRows, 0);
                } catch (excelError) {
                    console.error('Excel parsing error:', excelError);
                    toastr.error('Error parsing Excel file: ' + excelError.message);
                }
            } else {
                toastr.error('Please upload a CSV or Excel file');
            }
        } catch (error) {
            toastr.error('Error uploading template: ' + error.message);
        }
    };

    // Process CSV with specified header row
    processCSVWithHeaderRow = (allRows, headerRowIndex) => {
        if (headerRowIndex >= allRows.length) {
            toastr.error('Header row index exceeds the number of rows in the file');
            return;
        }

        // Get headers from specified row
        const headers = allRows[headerRowIndex].filter((header) => header && header.trim() !== '');

        // Get data rows (everything after header row)
        const dataRows = allRows.slice(headerRowIndex + 1);

        // Convert to object format
        const convertedRows = dataRows.map((row) => {
            const rowObject = {};
            headers.forEach((header, index) => {
                if (header) {
                    rowObject[header] = row[index] || '';
                }
            });
            return rowObject;
        });

        this.setState({
            fileRows: convertedRows,
            rowCount: convertedRows.length,
            columnCount: headers.length,
            headerRowIndex: headerRowIndex
        });

        // Analyze columns after successful processing
        this.analyzeColumns(convertedRows, headers);
        toastr.success('Template processed successfully');
    };

    // Process Excel with specified header row
    processExcelWithHeaderRow = (allRows, headerRowIndex) => {
        if (headerRowIndex >= allRows.length) {
            toastr.error('Header row index exceeds the number of rows in the file');
            return;
        }

        // Get headers from specified row
        const headers = allRows[headerRowIndex].map((header) => (header ? header.toString() : ''));
        const validHeaders = headers.filter((header) => header && header.trim() !== '');

        // Get data rows (everything after header row)
        const dataRows = allRows.slice(headerRowIndex + 1);

        // Convert Excel data to object format
        const convertedRows = dataRows.map((row) => {
            const rowObject = {};
            headers.forEach((header, index) => {
                if (header && header.trim() !== '') {
                    const cellValue = row[index];
                    rowObject[header] = cellValue !== null && cellValue !== undefined ? cellValue.toString() : '';
                }
            });
            return rowObject;
        });

        this.setState({
            fileRows: convertedRows,
            rowCount: convertedRows.length,
            columnCount: validHeaders.length,
            headerRowIndex: headerRowIndex
        });

        // Analyze columns after successful processing
        this.analyzeColumns(convertedRows, validHeaders);
        toastr.success('Excel template processed successfully');
    };

    // Handle header row index change
    handleHeaderRowChange = (e) => {
        const newIndex = parseInt(e.target.value) || 0;
        this.setState({ headerRowIndex: newIndex });
    };

    // Reprocess file with new header row
    reprocessWithNewHeaderRow = () => {
        const { allRawRows, headerRowIndex, sourceFileName } = this.state;

        if (!allRawRows || allRawRows.length === 0) {
            toastr.error('No file data available to reprocess');
            return;
        }

        const fileExtension = sourceFileName.split('.').pop().toLowerCase();

        if (fileExtension === 'csv') {
            this.processCSVWithHeaderRow(allRawRows, headerRowIndex);
        } else if (fileExtension === 'xlsx' || fileExtension === 'xls') {
            this.processExcelWithHeaderRow(allRawRows, headerRowIndex);
        }
    };

    triggerFileInput = () => {
        document.getElementById('templateFileInput').click();
    };

    handleNext = () => {
        // Step 1: Column Analysis duplicate systemColumnName validation
        if (this.state.activeStep === 1) {
            // Build a count of systemColumnName values (excluding 'None')
            const systemColumnNameCounts = {};
            this.state.columnAnalysis.forEach((col) => {
                if (col.systemColumnName && col.systemColumnName !== 'None') {
                    systemColumnNameCounts[col.systemColumnName] =
                        (systemColumnNameCounts[col.systemColumnName] || 0) + 1;
                }
            });
            // Find duplicates
            const duplicates = Object.entries(systemColumnNameCounts).filter(([systemColumnName, count]) => count > 1);
            if (duplicates.length > 0) {
                // Compose a message listing the duplicate systemColumnName values
                const duplicateFields = duplicates
                    .map(([systemColumnName]) => `<li><b>${systemColumnName}</b></li>`)
                    .join('');
                Swal.fire({
                    icon: 'warning',
                    title: 'Duplicate Field Mapping',
                    html: `<div style='text-align:left;'>Multiple columns are mapped to the same field:<ul>${duplicateFields}</ul>Please ensure each field is mapped to only one column.</div>`,
                    confirmButtonColor: '#007bff',
                    customClass: {
                        popup: 'swal2-border-radius'
                    }
                });
                return; // Prevent navigation
            }
        } // Step 2: Configure Test Associations - check for duplicate and unmatched analytes
        if (this.state.activeStep === 2) {
            if (this.state.selectedTests.length === 0) {
                Swal.fire({
                    icon: 'warning',
                    title: 'No Tests Selected',
                    text: 'Please select at least one test to proceed.',
                    confirmButtonColor: '#007bff',
                    customClass: {
                        popup: 'swal2-border-radius'
                    }
                });
                return; // Prevent navigation
            }
            // Check for duplicate source analyte mappings
            const sourceAnalyteCounts = {};
            this.state.analyteMappings.forEach((mapping) => {
                if (mapping.sourceAnalyte && mapping.sourceAnalyte !== 'None') {
                    sourceAnalyteCounts[mapping.sourceAnalyte] = (sourceAnalyteCounts[mapping.sourceAnalyte] || 0) + 1;
                }
            });

            // Find duplicates
            const duplicateSourceAnalytes = Object.entries(sourceAnalyteCounts).filter(([source, count]) => count > 1);
            if (duplicateSourceAnalytes.length > 0) {
                // Compose a detailed message showing which system analytes are mapped to the same source
                const duplicateDetails = duplicateSourceAnalytes
                    .map(([source, count]) => {
                        const systemAnalytes = this.state.analyteMappings
                            .filter((mapping) => mapping.sourceAnalyte === source)
                            .map((mapping) => mapping.systemAnalyte);
                        return `<li><b>${source}</b> ← mapped from: ${systemAnalytes.join(', ')}</li>`;
                    })
                    .join('');

                Swal.fire({
                    icon: 'warning',
                    title: 'Duplicate Source Analyte Mapping',
                    html: `<div style='text-align:left;'>The following source analytes have multiple system analytes mapped to them:<ul>${duplicateDetails}</ul>Each source analyte should only be mapped from one system analyte. Please review and correct these mappings.</div>`,
                    confirmButtonColor: '#007bff',
                    customClass: {
                        popup: 'swal2-border-radius'
                    }
                });
                return; // Prevent navigation
            }

            // Find source analytes that are not matched (have 'None' as sourceAnalyte)
            const unmatchedAnalytes = this.state.analyteMappings.filter(
                (mapping) => !mapping.sourceAnalyte || mapping.sourceAnalyte === 'None'
            );

            console.log('Unmatched analytes:', unmatchedAnalytes);

            if (unmatchedAnalytes.length > 0) {
                // Compose a message listing the unmatched system analytes
                const unmatchedFields = unmatchedAnalytes
                    .map((mapping) => `<li><b>${mapping.systemAnalyte}</b></li>`)
                    .join('');
                Swal.fire({
                    icon: 'warning',
                    title: 'Unmatched System Analytes',
                    html: `<div style='text-align:left;'>The following system analytes are not matched to any source analytes:<ul>${unmatchedFields}</ul>Please map them to appropriate source analytes before continuing.</div>`,
                    confirmButtonColor: '#007bff',
                    customClass: {
                        popup: 'swal2-border-radius'
                    }
                });
                return; // Prevent automatic navigation
            }
        }

        this.proceedToNextStep();
    };

    proceedToNextStep = () => {
        if (this.state.activeStep < this.state.steps.length - 1) {
            const nextStep = this.state.activeStep + 1;
            this.setState({ activeStep: nextStep }, () => {
                // If moving to step 3 (Configure Test Associations), initialize analyte mappings
                if (nextStep === 2) {
                    this.createAnalyteMappings();
                }
            });
        }
    };

    handleBack = () => {
        if (this.state.activeStep > 0) {
            this.setState({ activeStep: this.state.activeStep - 1 });
        }
    };

    // Analyze column data types and values
    analyzeColumns = (data, fields) => {
        // Filter out columns that contain "index" (case-insensitive)
        // const filteredFields = fields.filter((sourceColumnName) => !sourceColumnName.toLowerCase().includes('index'));
        // const filteredFields = fields.filter((sourceColumnName) => sourceColumnName);

        // Build mapping: sanitized -> original
        const columnNameMap = {};
        fields.forEach((col) => {
            columnNameMap[sanitizeColumnName(col)] = col;
        });

        let usedMappings = {};
        const columnAnalysis = fields.map((sourceColumnName, index) => {
            const sourceColumnField = sanitizeColumnName(sourceColumnName);
            const values = data
                .map((row) => row[sourceColumnName])
                .filter((val) => val !== null && val !== undefined && val !== '');
            const uniqueValues = [...new Set(values)];
            const exampleValues = uniqueValues.slice(0, 10); // Get first 10 unique values
            const dataType = this.detectDataType(values);
            const systemColumnName = this.getDefaultMapping(sourceColumnName, usedMappings);
            if (systemColumnName !== 'None') usedMappings[systemColumnName] = true;

            return {
                index, // original
                sourceColumnName,
                sourceColumnField, // sanitized key
                systemColumnName,
                dataType,
                values: exampleValues.join(', '),
                exampleValues,
                manualEntry: this.isManualEntry(systemColumnName),
                required: this.isRequiredField(systemColumnName)
            };
        });

        // Calculate statistics
        const totalColumns = columnAnalysis.length;
        const autoDetected = columnAnalysis.filter((col) => col.systemColumnName !== 'None').length;
        const requiredFields = columnAnalysis.filter((col) => col.required).length;
        this.setState({
            columnAnalysis,
            totalColumns,
            autoDetected,
            requiredFields,
            columnNameMap
        });
    };

    // Detect data type based on values
    detectDataType = (values) => {
        if (values.length === 0) return 'Text';

        const sample = values.slice(0, 10); // Check first 10 values

        // Check if all values are numbers
        const allNumbers = sample.every((val) => !isNaN(val) && val !== '');
        if (allNumbers) return 'Number';

        // Check if all values are dates
        const allDates = sample.every((val) => !isNaN(Date.parse(val)));
        if (allDates) return 'Date';

        // Check if all values are boolean-like
        const booleanValues = ['true', 'false', 'yes', 'no', '1', '0'];
        const allBooleans = sample.every((val) => booleanValues.includes(val.toString().toLowerCase()));
        if (allBooleans) return 'Boolean';

        return 'Text';
    };

    // Get default mapping for common column names
    getDefaultMapping = (sourceColumnName, usedMappings = {}) => {
        const lowerName = sourceColumnName.toLowerCase();
        // Mapping rules

        const mappingRules = [
            {
                key: 'Sample ID',
                match: (n) => n.includes('sample') && (n.includes('id') || n.includes('id'))
            },
            { key: 'Sample Type', match: (n) => n.includes('sample') && n.includes('type') },
            { key: 'Collection Date', match: (n) => n.includes('acquisition') && /date|time/.test(n) },
            {
                key: 'Analyte Name',
                match: (n) => n.includes('component') && n.includes('name') && !n.includes('group')
            },
            {
                key: 'Analyte Result',
                match: (n) => n.includes('concentration') && n.includes('calculated') && !n.includes('is')
            },
            { key: 'Analyte Units', match: (n) => n.includes('unit') },
            { key: 'Analyte Area', match: (n) => n.includes('area') && !/is|height|ratio/.test(n) }
        ];
        for (const rule of mappingRules) {
            if (rule.match(lowerName)) {
                if (!usedMappings[rule.key]) {
                    return rule.key;
                } else {
                    return 'None';
                }
            }
        }
        return 'None';
    };

    // Check if field is typically required
    isRequiredField = (systemColumnName) => {
        const requiredFields = ['Sample ID', 'Analyte Name', 'Analyte Result', 'Analyte Units'];
        return requiredFields.includes(systemColumnName);
    };

    isManualEntry = (systemColumnName) => {
        const manualEntryFields = ['Analyte Result', 'Analyte Units', 'Analyte Area'];
        return manualEntryFields.includes(systemColumnName);
    };

    // AG Grid column definitions for column analysis
    getColumnAnalysisColumnDefs = () => {
        return [
            // {
            //     headerName: 'ID',
            //     field: 'index',
            //     minWidth: 150
            // },
            {
                headerName: 'Column Name',
                field: 'sourceColumnName',
                minWidth: 150,
                flex: 1
                // valueGetter: (params) =>
                //     this.state.columnNameMap[params.data.sourceColumnField] || params.data.sourceColumnName,
                // cellRenderer: (params) => (
                //     <span>
                //         {this.state.columnNameMap[params.data.sourceColumnField] || params.data.sourceColumnName}
                //     </span>
                // )
            },
            {
                headerName: 'Data Type',
                field: 'dataType',
                minWidth: 100,
                flex: 1,
                cellRenderer: (params) => {
                    const typeColors = {
                        Text: 'badge-secondary',
                        Number: 'badge-success',
                        Date: 'badge-info',
                        Boolean: 'badge-warning'
                    };
                    return (
                        <div
                            dangerouslySetInnerHTML={{
                                __html: params.value
                                    ? `<span class="badge ${typeColors[params.value] || 'badge-secondary'}">${
                                          params.value
                                      }</span>`
                                    : 'No field updated'
                            }}
                        ></div>
                    );
                }
            },
            {
                headerName: 'Values',
                field: 'values',
                minWidth: 150,
                flex: 1
            },
            {
                headerName: 'Map To',
                field: 'systemColumnName',
                minWidth: 150,
                flex: 1,
                cellEditor: 'agRichSelectCellEditor',
                cellEditorParams: {
                    values: [
                        'None',
                        'Sample ID',
                        'Sample Type',
                        'Collection Date',
                        'Analyte Name',
                        'Analyte Result',
                        'Analyte Units',
                        'Analyte Area'
                    ]
                },
                cellRenderer: (params) => {
                    return (
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                height: '100%',
                                padding: '0 8px'
                            }}
                        >
                            <span>{params.value || 'None'}</span>
                            <i
                                className="fas fa-chevron-down"
                                style={{
                                    color: '#6c757d',
                                    fontSize: '0.8em'
                                }}
                            ></i>
                        </div>
                    );
                },
                editable: true
            },
            {
                headerName: 'Manual Entry',
                field: 'manualEntry',
                minWidth: 120,
                flex: 1,
                cellRenderer: 'agCheckboxCellRenderer',
                cellEditor: 'agCheckboxCellEditor',
                editable: (params) => {
                    // Disable Manual Entry for Collection Date
                    return !['Collection Date', 'Sample ID', 'Sample Type', 'Analyte Name'].includes(
                        params.data.systemColumnName
                    );
                }
            },
            {
                headerName: 'Required',
                field: 'required',
                minWidth: 100,
                flex: 1,
                cellRenderer: 'agCheckboxCellRenderer',
                cellEditor: 'agCheckboxCellEditor',
                editable: true
            }
        ];
    };

    // Handle cell value changes in AG Grid (Column Analysis)
    onCellValueChanged = (event) => {
        // Update the changed row directly in the node data to avoid re-render
        const updatedAnalysis = [...this.state.columnAnalysis];
        // const rowIndex = event.node.rowIndex;
        // updatedAnalysis[rowIndex][event.colDef.field] = event.newValue;

        // Recalculate statistics
        const autoDetected = updatedAnalysis.filter((col) => col.systemColumnName !== 'None').length;
        const requiredFields = updatedAnalysis.filter((col) => col.required).length;

        // Update state without triggering grid refresh
        this.setState({
            // columnAnalysis: updatedAnalysis,
            autoDetected,
            requiredFields
        });
    };

    // Extract source analytes from parsed data
    extractSourceAnalytes = () => {
        const analyteNameColumn = this.state.columnAnalysis.find((col) => col.systemColumnName === 'Analyte Name');
        if (!analyteNameColumn || !this.state.fileRows.length) {
            return [];
        }

        const sourceColumnName = analyteNameColumn.sourceColumnName;
        const sourceAnalytes = [
            ...new Set(
                this.state.fileRows.map((row) => row[sourceColumnName]).filter((val) => val && val.trim() !== '')
            )
        ];

        return sourceAnalytes;
    };

    // Get all system analytes from selected tests
    getSystemAnalytes = () => {
        const systemAnalytes = [];
        const existedSystemAnalyteIds = this.state.analyteMappings.map((mapping) => mapping.systemAnalyteId);
        this.state.selectedTests.forEach((test) => {
            if (test.analyteIds && test.analyteIds.length > 0) {
                test.analyteIds.forEach((analyte) => {
                    if (existedSystemAnalyteIds.includes(analyte._id)) return; // Skip if already mapped
                    systemAnalytes.push({
                        _id: analyte._id,
                        name: analyte.name
                    });
                });
            }
        });
        // Deduplicate by _id and name
        const seen = new Set();
        const deduped = systemAnalytes.filter((item) => {
            const key = item._id + '|' + item.name;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
        return deduped;
    };

    // Auto-map system analytes to source analytes (reversed logic)
    createAnalyteMappings = () => {
        const sourceAnalytes = this.extractSourceAnalytes();
        const systemAnalytes = this.getSystemAnalytes();
        // For each system analyte, try to find a matching source analyte
        const mappings = systemAnalytes.map((sysAnalyte) => {
            // Try to find matching source analyte (case-insensitive, partial match)
            const matchedSource = sourceAnalytes.find(
                (src) => src && src.toLowerCase() === sysAnalyte.name?.toLowerCase()
                // (src.toLowerCase().includes(sysAnalyte.name.toLowerCase()) ||
                //     sysAnalyte.name.toLowerCase().includes(src.toLowerCase()))
            );
            return {
                systemAnalyte: sysAnalyte.name,
                systemAnalyteId: sysAnalyte._id,
                sourceAnalyte: matchedSource || 'None'
            };
        });

        this.setState({
            sourceAnalytes, // still keep for summary
            analyteMappings: [...this.state.analyteMappings, ...mappings]
        });
    };

    // Enhanced handler for checkbox + All logic
    handleTestSelectionChange = (event, value, reason) => {
        // If user clicked the 'All' option
        if (value.length > 0 && value[value.length - 1] && value[value.length - 1].allSelector) {
            if (this.state.selectedTests.length === this.state.allTests.length) {
                // Deselect all
                this.setState({ selectedTests: [] }, () => this.createAnalyteMappings());
            } else {
                // Select all
                this.setState({ selectedTests: [...this.state.allTests] }, () => this.createAnalyteMappings());
            }
            return;
        }
        // Normal selection
        this.setState({ selectedTests: value }, () => this.createAnalyteMappings());
    };

    // AG Grid column definitions for analyte mapping
    getAnalyteMappingColumnDefs = () => {
        const sourceAnalytes = this.extractSourceAnalytes();
        const analyteOptions = ['None', ...sourceAnalytes];

        return [
            {
                headerName: 'System Analytes',
                field: 'systemAnalyte',
                minWidth: 200,
                flex: 1,
                editable: false
            },
            {
                headerName: 'Source Analytes',
                field: 'sourceAnalyte',
                minWidth: 200,
                flex: 1,
                cellEditor: 'agRichSelectCellEditor',
                cellEditorParams: {
                    values: analyteOptions
                },
                cellRenderer: (params) => {
                    const isUnmatched = !params.value || params.value === 'None';
                    return (
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                height: '100%',
                                padding: '0 8px'
                            }}
                        >
                            <span
                                style={{
                                    color: isUnmatched ? '#dc3545' : 'inherit',
                                    fontWeight: isUnmatched ? 'bold' : 'normal'
                                }}
                            >
                                {params.value || 'None'}
                            </span>
                            <i
                                className="fas fa-chevron-down"
                                style={{
                                    color: '#6c757d',
                                    fontSize: '0.8em'
                                }}
                            ></i>
                        </div>
                    );
                },
                editable: true
            }
        ];
    };

    // Handle analyte mapping cell value changes
    onAnalyteMappingCellValueChanged = (event) => {};

    // Column defs for Field Mapping Summary sample preview
    getFieldMappingSummaryColumnDefs = () => {
        // Show all mapped columns (systemColumnName !== 'None')
        const visibleCols = this.state.columnAnalysis.filter((col) => col.systemColumnName !== 'None');
        return visibleCols.map((col) => ({
            headerName: col.sourceColumnName, // original name for display
            field: col.sourceColumnField, // sanitized key for AG Grid
            minWidth: 120,
            flex: 1
        }));
    };

    // Get 5 sample rows for Field Mapping Summary
    getFieldMappingSampleRows = () => {
        if (!this.state.fileRows || this.state.fileRows.length === 0) return [];
        // Only include columns that are mapped (systemColumnName !== 'None')
        const visibleCols = this.state.columnAnalysis
            .filter((col) => col.systemColumnName !== 'None')
            .map((col) => ({ sourceColumnField: col.sourceColumnField, original: col.sourceColumnName }));
        // Take first 5 rows
        return this.state.fileRows.slice(0, 5).map((row) => {
            const filtered = {};
            visibleCols.forEach(({ sourceColumnField, original }) => {
                filtered[sourceColumnField] = row[original];
            });
            return filtered;
        });
    };

    render() {
        return (
            <div>
                <div className="row form-row mt-2">
                    <div className="col-12 d-flex justify-content-center">
                        <Stepper activeStep={this.state.activeStep} alternativeLabel className="w-100">
                            {this.state.steps.map((label, index) => {
                                return (
                                    <Step key={label}>
                                        <StepLabel>{label}</StepLabel>
                                    </Step>
                                );
                            })}
                        </Stepper>
                    </div>
                </div>

                {/* Upload Template Section - Step 0 */}
                {this.state.activeStep === 0 && (
                    <div className="row form-row" style={{ marginTop: '20px' }}>
                        <div className="col-12">
                            <div className="card">
                                <div className="card-body">
                                    <div className="row">
                                        <div className="col-12">
                                            <h4 className="card-title mb-1">Upload Template File</h4>
                                            <p>
                                                Upload a sample CSV/Excel file that represents your standard result
                                                format. This will be used to create a reusable template for future
                                                imports and manual entry.
                                            </p>
                                        </div>
                                        <div className="col-12">
                                            <div className="form-group">
                                                <div className="file-upload-wrapper">
                                                    <div
                                                        className="file-upload-dropzone"
                                                        onClick={this.triggerFileInput}
                                                        style={{
                                                            border: '2px dashed #ddd',
                                                            borderRadius: '8px',
                                                            padding: '20px',
                                                            textAlign: 'center',
                                                            cursor: 'pointer',
                                                            transition: 'all 0.3s ease',
                                                            backgroundColor: '#f8f9fa'
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.target.style.borderColor = '#007bff';
                                                            e.target.style.backgroundColor = '#e3f2fd';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.target.style.borderColor = '#ddd';
                                                            e.target.style.backgroundColor = '#f8f9fa';
                                                        }}
                                                    >
                                                        <i
                                                            className="fas fa-cloud-upload-alt"
                                                            style={{
                                                                fontSize: '2rem',
                                                                color: '#6c757d',
                                                                marginBottom: '10px'
                                                            }}
                                                        ></i>
                                                        <div>
                                                            <strong>
                                                                Drop your template file here or click to browse
                                                            </strong>
                                                        </div>
                                                        <small className="text-muted">
                                                            CSV, Excel (.xlsx, .xls) files supported
                                                        </small>
                                                    </div>
                                                    <input
                                                        id="templateFileInput"
                                                        type="file"
                                                        style={{ display: 'none' }}
                                                        accept=".csv,.xlsx,.xls"
                                                        onChange={this.uploadTemplate}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {this.state.sourceFileName && (
                                        <div className="mt-4">
                                            <div className="alert alert-success" role="alert">
                                                <h6 className="alert-heading">
                                                    <i className="fas fa-check-circle mr-2"></i>
                                                    Template Successfully Uploaded
                                                </h6>
                                                <hr />
                                                <div className="row">
                                                    <div className="col-12">
                                                        <div className="form-group">
                                                            <label className="form-label">Template Name</label>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                placeholder="Enter template name"
                                                                value={this.state.templateName}
                                                                onChange={(e) =>
                                                                    this.setState({
                                                                        templateName: e.target.value
                                                                    })
                                                                }
                                                            />
                                                        </div>
                                                    </div>

                                                    {/* Header Row Configuration */}
                                                    {this.state.showHeaderRowInput && (
                                                        <div className="col-12">
                                                            <div className="card border-info mb-3">
                                                                <div className="card-body">
                                                                    <h6 className="card-title text-info">
                                                                        <i className="fas fa-layer-group mr-2"></i>
                                                                        Header Row Configuration
                                                                    </h6>
                                                                    <p className="text-muted small">
                                                                        If your file has header information or metadata
                                                                        rows before the actual column headers, specify
                                                                        which row contains the column headers (0-based
                                                                        index).
                                                                    </p>
                                                                    <div className="row align-items-end">
                                                                        <div className="col-md-6">
                                                                            <div className="form-group mb-0">
                                                                                <label className="form-label">
                                                                                    Header Row Index
                                                                                </label>
                                                                                <input
                                                                                    type="number"
                                                                                    className="form-control"
                                                                                    min="0"
                                                                                    max={
                                                                                        this.state.allRawRows
                                                                                            ? this.state.allRawRows
                                                                                                  .length - 1
                                                                                            : 0
                                                                                    }
                                                                                    value={this.state.headerRowIndex}
                                                                                    onChange={
                                                                                        this.handleHeaderRowChange
                                                                                    }
                                                                                    placeholder="0"
                                                                                />
                                                                                <small className="form-text text-muted">
                                                                                    Row 0 is the first row. Current file
                                                                                    has{' '}
                                                                                    {this.state.allRawRows
                                                                                        ? this.state.allRawRows.length
                                                                                        : 0}{' '}
                                                                                    total rows.
                                                                                </small>
                                                                            </div>
                                                                        </div>
                                                                        <div className="col-md-6">
                                                                            <Button
                                                                                variant="info"
                                                                                size="sm"
                                                                                onClick={this.reprocessWithNewHeaderRow}
                                                                                className="mb-3"
                                                                            >
                                                                                <i className="fas fa-sync-alt mr-2"></i>
                                                                                Reprocess File
                                                                            </Button>
                                                                        </div>
                                                                    </div>

                                                                    {/* Preview of first few rows */}
                                                                    {this.state.allRawRows &&
                                                                        this.state.allRawRows.length > 0 && (
                                                                            <div className="mt-3">
                                                                                <p className="text-muted small mb-2">
                                                                                    <strong>
                                                                                        Preview of first 5 rows:
                                                                                    </strong>
                                                                                </p>
                                                                                <div style={{ overflowX: 'auto' }}>
                                                                                    <table
                                                                                        className="table table-sm table-bordered"
                                                                                        style={{ fontSize: '0.85em' }}
                                                                                    >
                                                                                        <tbody>
                                                                                            {this.state.allRawRows
                                                                                                .slice(0, 5)
                                                                                                .map((row, index) => (
                                                                                                    <tr
                                                                                                        key={index}
                                                                                                        className={
                                                                                                            index ===
                                                                                                            this.state
                                                                                                                .headerRowIndex
                                                                                                                ? 'table-info'
                                                                                                                : ''
                                                                                                        }
                                                                                                    >
                                                                                                        <td
                                                                                                            className="text-center"
                                                                                                            style={{
                                                                                                                width: '50px'
                                                                                                            }}
                                                                                                        >
                                                                                                            <strong>
                                                                                                                {index}
                                                                                                            </strong>
                                                                                                        </td>
                                                                                                        {row
                                                                                                            .slice(
                                                                                                                0,
                                                                                                                10
                                                                                                            )
                                                                                                            .map(
                                                                                                                (
                                                                                                                    cell,
                                                                                                                    cellIndex
                                                                                                                ) => (
                                                                                                                    <td
                                                                                                                        key={
                                                                                                                            cellIndex
                                                                                                                        }
                                                                                                                    >
                                                                                                                        {cell !==
                                                                                                                            null &&
                                                                                                                        cell !==
                                                                                                                            undefined
                                                                                                                            ? cell.toString()
                                                                                                                            : ''}
                                                                                                                    </td>
                                                                                                                )
                                                                                                            )}
                                                                                                        {row.length >
                                                                                                            10 && (
                                                                                                            <td>...</td>
                                                                                                        )}
                                                                                                    </tr>
                                                                                                ))}
                                                                                        </tbody>
                                                                                    </table>
                                                                                </div>
                                                                                <p className="text-info small mb-0">
                                                                                    <i className="fas fa-info-circle mr-1"></i>
                                                                                    The highlighted row (row{' '}
                                                                                    {this.state.headerRowIndex}) will be
                                                                                    used as column headers.
                                                                                </p>
                                                                            </div>
                                                                        )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}

                                                    <div className="col-12">
                                                        <div className="file-info-item mt-2">
                                                            <strong>File:</strong>
                                                            <span className="ml-2">{this.state.sourceFileName}</span>
                                                        </div>
                                                        <div className="file-info-item">
                                                            <strong>Size:</strong>
                                                            <span className="ml-2">
                                                                {(this.state.originalSize / 1024).toFixed(2)} KB
                                                            </span>
                                                        </div>
                                                        <div className="file-info-item">
                                                            <strong>Data Rows:</strong>
                                                            <span className="ml-2">{this.state.rowCount}</span>
                                                            {this.state.headerRowIndex > 0 && (
                                                                <span className="text-muted ml-1">
                                                                    (skipped {this.state.headerRowIndex} header rows)
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="file-info-item">
                                                            <strong>Columns:</strong>
                                                            <span className="ml-2">{this.state.columnCount}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Column Analysis Step - Step 1 */}
                {this.state.activeStep === 1 && (
                    <div className="row form-row" style={{ marginTop: '20px' }}>
                        <div className="col-12">
                            <div className="card">
                                <div className="card-body">
                                    <h4 className="card-title mb-3">
                                        <i className="fas fa-analytics mr-2"></i>
                                        Column Analysis & Auto-Detection
                                    </h4>

                                    {/* Statistics Cards */}
                                    <div className="row mb-4">
                                        <div className="col-md-3">
                                            <div className="card bg-primary text-white">
                                                <div className="card-body text-center">
                                                    <h3 className="mb-0">{this.state.totalColumns}</h3>
                                                    <small>Total Columns</small>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className="card bg-success text-white">
                                                <div className="card-body text-center">
                                                    <h3 className="mb-0">{this.state.autoDetected}</h3>
                                                    <small>Auto-Detected</small>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className="card bg-warning text-white">
                                                <div className="card-body text-center">
                                                    <h3 className="mb-0">{this.state.requiredFields}</h3>
                                                    <small>Required Fields</small>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className="card bg-info text-white">
                                                <div className="card-body text-center">
                                                    <h3 className="mb-0">{this.state.rowCount}</h3>
                                                    <small>Sample Records</small>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* AG Grid for Column Analysis */}
                                    <div style={{ height: '400px', width: '100%' }}>
                                        <ThemeContext.Consumer>
                                            {({ themeName }) => (
                                                <AgGridReact
                                                    columnDefs={this.getColumnAnalysisColumnDefs()}
                                                    rowData={this.state.columnAnalysis}
                                                    theme={themeName === 'Light' ? themeLight : themeDark}
                                                    onCellValueChanged={this.onCellValueChanged}
                                                    defaultColDef={{
                                                        resizable: true,
                                                        sortable: true,
                                                        filter: true
                                                    }}
                                                    rowSelection="multiple"
                                                    suppressScrollOnNewData={true}
                                                    suppressAnimationFrame={true}
                                                    maintainColumnOrder={true}
                                                    getRowId={(params) => params.data.index}
                                                    immutableData={true}
                                                    suppressRowTransform={true}
                                                />
                                            )}
                                        </ThemeContext.Consumer>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Configure Test Associations Step - Step 2 */}
                {this.state.activeStep === 2 && (
                    <div className="row form-row" style={{ marginTop: '20px' }}>
                        <div className="col-12">
                            <div className="card">
                                <div className="card-body">
                                    <h4 className="card-title mb-3">
                                        <i className="fas fa-cogs mr-2"></i>
                                        Configure Test Associations
                                    </h4>

                                    {/* Available Tests AutoComplete */}
                                    <div className="row mb-4">
                                        <div className="col-12">
                                            <label className="form-label">Available Tests</label>
                                            <Autocomplete
                                                multiple
                                                disableCloseOnSelect
                                                options={(() => {
                                                    // Insert a fake 'All' option at the top
                                                    const allOption = {
                                                        name: 'All',
                                                        _id: '__all__',
                                                        allSelector: true
                                                    };
                                                    return [allOption, ...this.state.allTests];
                                                })()}
                                                getOptionLabel={(option) => option.name || ''}
                                                value={this.state.selectedTests}
                                                onChange={this.handleTestSelectionChange}
                                                isOptionEqualToValue={(option, value) => option._id === value._id}
                                                renderOption={(props, option, { selected }) => {
                                                    if (option.allSelector) {
                                                        // Render the 'All' option
                                                        const allSelected =
                                                            this.state.selectedTests.length ===
                                                                this.state.allTests.length &&
                                                            this.state.allTests.length > 0;
                                                        const someSelected =
                                                            this.state.selectedTests.length > 0 &&
                                                            this.state.selectedTests.length <
                                                                this.state.allTests.length;
                                                        return (
                                                            <li {...props} key="all-tests">
                                                                <Checkbox
                                                                    indeterminate={someSelected}
                                                                    checked={allSelected}
                                                                    style={{ marginRight: 8 }}
                                                                />
                                                                <span style={{ fontWeight: 'bold' }}>All</span>
                                                            </li>
                                                        );
                                                    }
                                                    // Render normal test option
                                                    return (
                                                        <li {...props} key={option._id}>
                                                            <Checkbox checked={selected} style={{ marginRight: 8 }} />
                                                            <div>
                                                                <div style={{ fontWeight: 'bold' }}>{option.name}</div>
                                                                <div style={{ fontSize: '0.85em', color: '#6c757d' }}>
                                                                    {option.analyteIds?.length || 0} analytes
                                                                </div>
                                                            </div>
                                                        </li>
                                                    );
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        variant="outlined"
                                                        placeholder="Select tests to associate with this template"
                                                        fullWidth
                                                    />
                                                )}
                                                style={{ marginBottom: '20px' }}
                                            />
                                        </div>
                                    </div>

                                    {/* Instrument AutoComplete */}
                                    <div className="row mb-4">
                                        <div className="col-12">
                                            <label className="form-label">Select Instrument</label>
                                            <Autocomplete
                                                options={this.state.allInstruments}
                                                getOptionLabel={(option) => option.name || ''}
                                                value={this.state.selectedInstrument}
                                                onChange={(event, newValue) => {
                                                    this.setState({ selectedInstrument: newValue });
                                                }}
                                                isOptionEqualToValue={(option, value) => option._id === value._id}
                                                renderOption={(props, option) => (
                                                    <li {...props} key={option._id}>
                                                        <div>
                                                            <div style={{ fontWeight: 'bold' }}>{option.name}</div>
                                                            <div style={{ fontSize: '0.85em', color: '#6c757d' }}>
                                                                {option.type} - {option.serialNumber}
                                                            </div>
                                                        </div>
                                                    </li>
                                                )}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        variant="outlined"
                                                        placeholder="Select instrument for this template"
                                                        fullWidth
                                                    />
                                                )}
                                            />
                                        </div>
                                    </div>

                                    {/* Analyte Mapping Grid */}
                                    {this.state.selectedTests.length > 0 && this.state.analyteMappings.length > 0 && (
                                        <div>
                                            <h6 className="mb-3">
                                                <i className="fas fa-exchange-alt mr-2"></i>
                                                Analyte Mapping
                                            </h6>
                                            <div style={{ height: '400px', width: '100%' }}>
                                                <ThemeContext.Consumer>
                                                    {({ themeName }) => (
                                                        <AgGridReact
                                                            columnDefs={this.getAnalyteMappingColumnDefs()}
                                                            rowData={this.state.analyteMappings}
                                                            theme={themeName === 'Light' ? themeLight : themeDark}
                                                            onCellValueChanged={this.onAnalyteMappingCellValueChanged}
                                                            defaultColDef={{
                                                                resizable: true,
                                                                sortable: true,
                                                                filter: true
                                                            }}
                                                            rowSelection="single"
                                                            suppressScrollOnNewData={true}
                                                            suppressAnimationFrame={true}
                                                            maintainColumnOrder={true}
                                                            getRowId={(params) =>
                                                                params.data.systemAnalyteId || params.data.systemAnalyte
                                                            }
                                                            immutableData={true}
                                                            suppressRowTransform={true}
                                                        />
                                                    )}
                                                </ThemeContext.Consumer>
                                            </div>
                                        </div>
                                    )}

                                    {this.state.selectedTests.length === 0 && (
                                        <div className="alert alert-info" role="alert">
                                            <i className="fas fa-info-circle mr-2"></i>
                                            Please select at least one test to see analyte mapping options.
                                        </div>
                                    )}

                                    {this.state.selectedTests.length > 0 && this.state.analyteMappings.length === 0 && (
                                        <div className="alert alert-warning" role="alert">
                                            <i className="fas fa-exclamation-triangle mr-2"></i>
                                            No analyte columns found in your uploaded file. Please ensure your file
                                            contains analyte name data and that it's properly mapped in the previous
                                            step.
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Field Mapping Summary Step - Step 3 */}
                {this.state.activeStep === 3 && (
                    <div className="row form-row" style={{ marginTop: '20px' }}>
                        <div className="col-12">
                            <div className="card">
                                <div className="card-body">
                                    <h4 className="card-title mb-3">
                                        <i className="fas fa-table mr-2"></i>
                                        Field Mapping Summary
                                    </h4>

                                    {/* Statistics Cards */}
                                    <div className="row mb-4">
                                        <div className="col-md-3">
                                            <div className="card bg-primary text-white">
                                                <div className="card-body text-center">
                                                    <h3 className="mb-0">
                                                        {
                                                            this.state.columnAnalysis.filter(
                                                                (col) => col.systemColumnName !== 'None'
                                                            ).length
                                                        }
                                                    </h3>
                                                    <small>Mapped Fields</small>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className="card bg-info text-white">
                                                <div className="card-body text-center">
                                                    <h3 className="mb-0">
                                                        {
                                                            this.state.columnAnalysis.filter((col) => col.manualEntry)
                                                                .length
                                                        }
                                                    </h3>
                                                    <small>Manual Entry Fields</small>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className="card bg-warning text-white">
                                                <div className="card-body text-center">
                                                    <h3 className="mb-0">
                                                        {this.state.columnAnalysis.filter((col) => col.required).length}
                                                    </h3>
                                                    <small>Required Fields</small>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className="card bg-secondary text-white">
                                                <div className="card-body text-center">
                                                    <h3 className="mb-0">
                                                        {
                                                            this.state.columnAnalysis.filter(
                                                                (col) => col.systemColumnName === 'None'
                                                            ).length
                                                        }
                                                    </h3>
                                                    <small>Ignored Fields</small>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Sample Data Preview AG Grid */}
                                    <h6 className="mb-2">Sample Data Preview</h6>
                                    <div style={{ height: '300px', width: '100%' }}>
                                        <ThemeContext.Consumer>
                                            {({ themeName }) => (
                                                <AgGridReact
                                                    columnDefs={this.getFieldMappingSummaryColumnDefs()}
                                                    rowData={this.getFieldMappingSampleRows()}
                                                    theme={themeName === 'Light' ? themeLight : themeDark}
                                                    defaultColDef={{
                                                        resizable: true,
                                                        sortable: true,
                                                        filter: true
                                                    }}
                                                    rowSelection="single"
                                                />
                                            )}
                                        </ThemeContext.Consumer>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Other Steps Content */}
                {this.state.activeStep === 4 && (
                    <div
                        className="row form-row justify-content-center"
                        style={{ marginTop: '32px', marginBottom: '32px' }}
                    >
                        <div className="col-12 d-flex justify-content-center">
                            <div
                                className="border-0"
                                style={{
                                    // maxWidth: 520,
                                    width: '100%'
                                    //
                                }}
                            >
                                <div className="card-body px-4 py-5 text-center">
                                    <div className="mb-4">
                                        <i
                                            className="fas fa-check-circle"
                                            style={{ color: '#28a745', fontSize: '2.5rem' }}
                                        ></i>
                                    </div>
                                    <h2
                                        className="card-title mb-2"
                                        style={{
                                            fontWeight: 700,
                                            color: '#28a745',
                                            textAlign: 'center',
                                            letterSpacing: '0.5px'
                                        }}
                                    >
                                        Template Configuration Complete!
                                    </h2>
                                    <h5
                                        className="mb-4"
                                        style={{ color: '#555', textAlign: 'center', fontWeight: 400 }}
                                    >
                                        Your template is ready to use for imports and manual entry
                                    </h5>

                                    {/* Template Summary as Card with List */}
                                    <div className="d-flex justify-content-center align-items-center flex-column">
                                        <div
                                            className="mb-4 p-4 card shadow-sm border-0"
                                            style={{
                                                maxWidth: 600,
                                                width: '100%',
                                                background: '#f9fafb' // lighter grey
                                            }}
                                        >
                                            <h6
                                                className="mb-3 text-center"
                                                style={{ fontWeight: 600, color: '#007bff', letterSpacing: '0.5px' }}
                                            >
                                                <i className="fas fa-list-alt mr-2"></i>Template Summary
                                            </h6>
                                            <div
                                                className=""
                                                style={{
                                                    borderRadius: '10px',
                                                    background: '#f9fafb'
                                                }}
                                            >
                                                <div className="card-body p-0">
                                                    <ul className="list-group list-group-flush">
                                                        <li
                                                            className="list-group-item d-flex justify-content-between align-items-center"
                                                            style={{
                                                                border: 'none',
                                                                borderBottom: '1px solid #e3e6ea',
                                                                padding: '0.75rem 1rem',
                                                                background: '#f9fafb'
                                                            }}
                                                        >
                                                            <span style={{ color: '#6c757d' }}>
                                                                <strong>Template Name</strong>
                                                            </span>
                                                            <span style={{ fontWeight: 500 }}>
                                                                {this.state.templateName || this.state.name}
                                                            </span>
                                                        </li>
                                                        <li
                                                            className="list-group-item d-flex justify-content-between align-items-center"
                                                            style={{
                                                                border: 'none',
                                                                borderBottom: '1px solid #e3e6ea',
                                                                padding: '0.75rem 1rem',
                                                                background: '#f9fafb'
                                                            }}
                                                        >
                                                            <span style={{ color: '#6c757d' }}>
                                                                <strong>Total Columns</strong>
                                                            </span>
                                                            <span style={{ fontWeight: 500 }}>
                                                                {this.state.totalColumns}
                                                            </span>
                                                        </li>
                                                        <li
                                                            className="list-group-item d-flex justify-content-between align-items-center"
                                                            style={{
                                                                border: 'none',
                                                                borderBottom: '1px solid #e3e6ea',
                                                                padding: '0.75rem 1rem',
                                                                background: '#f9fafb'
                                                            }}
                                                        >
                                                            <span style={{ color: '#6c757d' }}>
                                                                <strong>Mapped Fields</strong>
                                                            </span>
                                                            <span style={{ fontWeight: 500, color: '#28a745' }}>
                                                                {
                                                                    this.state.columnAnalysis.filter(
                                                                        (col) => col.systemColumnName !== 'None'
                                                                    ).length
                                                                }
                                                            </span>
                                                        </li>
                                                        <li
                                                            className="list-group-item d-flex justify-content-between align-items-center"
                                                            style={{
                                                                border: 'none',
                                                                borderBottom: '1px solid #e3e6ea',
                                                                padding: '0.75rem 1rem',
                                                                background: '#f9fafb'
                                                            }}
                                                        >
                                                            <span style={{ color: '#6c757d' }}>
                                                                <strong>Manual Entry Fields</strong>
                                                            </span>
                                                            <span style={{ fontWeight: 500, color: '#ffc107' }}>
                                                                {
                                                                    this.state.columnAnalysis.filter(
                                                                        (col) => col.manualEntry
                                                                    ).length
                                                                }
                                                            </span>
                                                        </li>
                                                        <li
                                                            className="list-group-item d-flex justify-content-between align-items-center"
                                                            style={{
                                                                border: 'none',
                                                                borderBottom: '1px solid #e3e6ea',
                                                                padding: '0.75rem 1rem',
                                                                background: '#f9fafb'
                                                            }}
                                                        >
                                                            <span style={{ color: '#6c757d' }}>
                                                                <strong>Associated Tests</strong>
                                                            </span>
                                                            <span style={{ fontWeight: 500 }}>
                                                                {(() => {
                                                                    const names = this.state.selectedTests
                                                                        .map((t) => t.name)
                                                                        .join(', ');
                                                                    if (!names) return '-';
                                                                    return names.length > 30
                                                                        ? names.slice(0, 30) + '…'
                                                                        : names;
                                                                })()}
                                                            </span>
                                                        </li>
                                                        <li
                                                            className="list-group-item d-flex justify-content-between align-items-center"
                                                            style={{
                                                                border: 'none',
                                                                padding: '0.75rem 1rem',
                                                                background: '#f9fafb'
                                                            }}
                                                        >
                                                            <span style={{ color: '#6c757d' }}>
                                                                <strong>Detected Analytes</strong>
                                                            </span>
                                                            <span style={{ fontWeight: 500, color: '#dc3545' }}>
                                                                {this.state.sourceAnalytes &&
                                                                this.state.sourceAnalytes.length > 0
                                                                    ? this.state.sourceAnalytes.length
                                                                    : 0}
                                                            </span>
                                                        </li>
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>

                                        {/* What's Next? */}
                                        <div
                                            className="mb-2"
                                            style={{
                                                maxWidth: 700,
                                                width: '100%'
                                            }}
                                        >
                                            <h6
                                                className="mb-3"
                                                style={{ fontWeight: 600, color: '#343a40', letterSpacing: '0.5px' }}
                                            >
                                                <i className="fas fa-lightbulb mr-2 text-warning"></i>What's Next?
                                            </h6>
                                            <div className="row justify-content-center">
                                                <div className="col-md-4 mb-3">
                                                    <div
                                                        className="card h-100 shadow-sm border-success"
                                                        style={{
                                                            borderLeft: '4px solid #28a745',
                                                            borderRadius: '10px'
                                                        }}
                                                    >
                                                        <div className="card-body text-center py-4">
                                                            <i className="fas fa-file-import fa-2x mb-2 text-success"></i>
                                                            <h5 className="card-title" style={{ fontWeight: 600 }}>
                                                                Import Result
                                                            </h5>
                                                            <p className="card-text" style={{ color: '#555' }}>
                                                                Use this template to import result files
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-md-4 mb-3">
                                                    <div
                                                        className="card h-100 shadow-sm border-primary"
                                                        style={{
                                                            borderLeft: '4px solid #007bff',
                                                            borderRadius: '10px'
                                                        }}
                                                    >
                                                        <div className="card-body text-center py-4">
                                                            <i className="fas fa-keyboard fa-2x mb-2 text-primary"></i>
                                                            <h5 className="card-title" style={{ fontWeight: 600 }}>
                                                                Manual Entry
                                                            </h5>
                                                            <p className="card-text" style={{ color: '#555' }}>
                                                                Enter results using configured fields
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-md-4 mb-3">
                                                    <div
                                                        className="card h-100 shadow-sm border-warning"
                                                        style={{
                                                            borderLeft: '4px solid #ffc107',
                                                            borderRadius: '10px'
                                                        }}
                                                    >
                                                        <div className="card-body text-center py-4">
                                                            <i className="fas fa-edit fa-2x mb-2 text-warning"></i>
                                                            <h5 className="card-title" style={{ fontWeight: 600 }}>
                                                                Edit Template
                                                            </h5>
                                                            <p className="card-text" style={{ color: '#555' }}>
                                                                Modify field mappings anytime
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="row">
                    <div
                        className="col-12 d-flex justify-content-between align-items-center"
                        style={{
                            paddingTop: '10px',
                            borderTop: '1px solid rgba(0,0,0,.2)'
                        }}
                    >
                        {this.state.activeStep > 0 && (
                            <Button style={{ float: 'left' }} variant="secondary" onClick={this.handleBack}>
                                Back
                            </Button>
                        )}
                        <div style={{ marginLeft: 'auto' }}>
                            {this.state.activeStep < this.state.steps.length - 1 ? (
                                <>
                                    {this.state.sourceFileName &&
                                        this.state.activeStep < this.state.steps.length - 1 && (
                                            <Button
                                                style={{ marginLeft: '10px' }}
                                                variant="primary"
                                                onClick={() => this.saveChanges(false)}
                                            >
                                                Save Draft
                                            </Button>
                                        )}
                                    <Button
                                        style={{ float: 'right', marginLeft: '10px' }}
                                        variant="primary"
                                        onClick={this.handleNext}
                                        disabled={this.state.activeStep === 0 && !this.state.sourceFileName}
                                    >
                                        Next
                                    </Button>
                                </>
                            ) : (
                                <Button
                                    style={{ float: 'right', marginLeft: '10px' }}
                                    variant="primary"
                                    onClick={() => this.saveChanges(true)}
                                >
                                    Save Changes
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}
